import { RenderChildren } from '@/node-editor/components';
import { INode, INodeProps } from '@/types/node';

import React from 'react';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import { IContent } from './contract';
import { useNodeProps } from '@/node-editor/hooks';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { rgbToCssColor } from '@/lib/utils/format';
import { getGridNumber } from '@/node-editor/utilities';
import { useRowContext } from '@/nodes/row/context';
interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

const useStyles = (
  props: Partial<IContent> & { override: Record<string, any> }
) => ({
  gridRoot: {
    ...props.override.root,
  },
  container: {
    display: 'flex !important',
    minHeight: `${
      props.minHeight === 0 ? 'auto' : `${props.minHeight}px`
    } !important`,
    flexDirection: 'column !important',
    justifyContent: `${props.alignment.vertical} !important`,
    alignItems: `${props.alignment.horizontal} !important`,
    position: 'relative !important',
    ...(props.textColor && { color: rgbToCssColor(props.textColor as any) }),
    ...(props.backgroundColor && {
      backgroundColor: rgbToCssColor(props.backgroundColor as any),
    }),
    '&>div': {
      position: 'relative !important',
    },
    overflow: 'hidden !important',
  },
  background: {
    backgroundImage: `url(${props.backgroundImage})`,
    backgroundSize: 'cover !important',
    backgroundRepeat: 'no-repeat !important',
    backgroundPosition: 'center center !important',
  },
  video: {
    position: 'absolute' as const,
    objectFit: 'cover' as const,
    minWidth: '100%',
    minHeight: '100%',
    width: '100%',
    height: 'auto',
    backgroundSize: 'cover',
    overflow: 'hidden',
    display: 'block',
  },
});

export const Render = ({
  node: {
    id,
    override,
    content: {
      alignment,
      backgroundColor,
      backgroundImage,
      maxWidth,
      textColor,
      minHeight = 0,
      videoId,
    },
  },
  children,
}: IProps) => {
  const theme = useTheme();
  const rowContext = useRowContext();
  const classes = useStyles({
    alignment,
    backgroundColor,
    backgroundImage: getImage(backgroundImage, ImageSize.Large),
    maxWidth,
    textColor,
    minHeight,
    override,
  });

  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const videoUrl =
    videoId && videoId !== '-'
      ? `https://stream.mux.com/${videoId}/${smallScreen ? 'low' : 'high'}.mp4`
      : null;

  const gridSize = getGridNumber(maxWidth);
  const gridSizeNum = Number(gridSize) || 12;

  // Adjust width calculation to account for row spacing
  // Each spacing unit in MUI Grid is 8px by default
  // The row spacing is divided by 2 for each side, so we need to subtract it from the column width
  const rowSpacing = rowContext?.horizontalSpacing || 0;
  const spacingInPx = rowSpacing * 8; // Row spacing is already halved in the row component

  // Calculate the width percentage accounting for the spacing
  const widthPercentage = (gridSizeNum / 12) * 100;

  return (
    <Grid
      sx={{
        ...classes.gridRoot,
        width: {
          xs: '100%',
          md: `calc(${widthPercentage}% - ${spacingInPx}px)`,
        },
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: {
          xs: '100%',
          md: `calc(${widthPercentage}% - ${spacingInPx}px)`,
        },
      }}
      {...useNodeProps(id)}
    >
      <Box
        component="div"
        sx={{
          ...classes.container,
          ...(Boolean(backgroundImage) && classes.background),
        }}
      >
        {videoUrl && (
          <video playsInline muted loop autoPlay style={classes.video}>
            {/* <source src="https://cdn2.hubspot.net/hubfs/4149027/WSAI2018%20-%20AfterMovie.mp4" /> */}
            <source src={videoUrl} />
          </video>
        )}
        <RenderChildren>{children}</RenderChildren>
      </Box>
    </Grid>
  );
};
